/**
 * Gera um ID estável para botão de template baseado no texto.
 * Usa djb2 hash puro — sobrevive reordenações de botões na Meta.
 *
 * Com max 3 botões QUICK_REPLY e textos distintos, colisão é
 * virtualmente impossível. O hash é determinístico e idêntico
 * entre client e server.
 *
 * Compartilhado entre client (ContentNode, NodeConfigPanel) e server (engine).
 */
export function stableButtonId(text: string): string {
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    hash = ((hash << 5) - hash + text.charCodeAt(i)) | 0;
  }
  return `tpl-${Math.abs(hash).toString(36)}`;
}

/**
 * Qual bloco começa o fluxo.
 *
 * Três lugares precisam concordar: o `isStart` que o motor grava, a seta do
 * cartão "Começa quando" no canvas e a checagem de bloco órfão. Se
 * divergirem, o desenho mostra a entrada num lugar e o contato entra por
 * outro.
 *
 * Duas heurísticas simples erram, cada uma num desenho comum:
 *
 *  · "o primeiro da lista" (o antigo `isStart: i === 0`) erra quando a pessoa
 *    acrescenta depois um passo ANTES do que já existia — a ordem do array é
 *    de criação, não de execução;
 *  · "quem não recebe aresta" erra no desenho "volta ao menu", onde o bloco
 *    de abertura recebe a seta do menu: aí qualquer trecho largado solto no
 *    canvas toma a entrada, e a execução começa num nó morto sem que ninguém
 *    seja avisado (o painel de pendências isenta a entrada da checagem de
 *    órfão).
 *
 * O que separa os dois casos é o COMPONENTE: o fluxo de verdade é o maior
 * conjunto de blocos ligados entre si. Dentro dele, a entrada é quem não
 * recebe aresta; se todos recebem (o fluxo volta ao começo), é o primeiro
 * dele na ordem de criação.
 */
export function acharNoDeEntrada<T extends { id: string }>(
  nos: T[],
  arestas: Array<{ source: string; target: string }>,
): T | undefined {
  if (nos.length === 0) return undefined;
  const existe = new Set(nos.map((n) => n.id));

  // Componentes por ligação, ignorando a direção: dois blocos estão no mesmo
  // fluxo se há um caminho entre eles, para qualquer lado.
  const vizinhos = new Map<string, string[]>(nos.map((n) => [n.id, []]));
  for (const a of arestas) {
    if (!existe.has(a.source) || !existe.has(a.target)) continue;
    vizinhos.get(a.source)!.push(a.target);
    vizinhos.get(a.target)!.push(a.source);
  }

  const visitado = new Set<string>();
  let melhor: T[] = [];
  for (const inicio of nos) {
    if (visitado.has(inicio.id)) continue;
    const componente: string[] = [];
    const fila = [inicio.id];
    visitado.add(inicio.id);
    while (fila.length > 0) {
      const id = fila.shift()!;
      componente.push(id);
      for (const v of vizinhos.get(id) ?? []) {
        if (visitado.has(v)) continue;
        visitado.add(v);
        fila.push(v);
      }
    }
    // Mantém a ordem de criação dentro do componente.
    const emOrdem = nos.filter((n) => componente.includes(n.id));
    // Empate vai para o primeiro encontrado, que é o que contém o bloco mais
    // antigo — e portanto o fluxo que a pessoa começou a montar.
    if (emOrdem.length > melhor.length) melhor = emOrdem;
  }

  const recebe = new Set(arestas.map((a) => a.target));
  return melhor.find((n) => !recebe.has(n.id)) ?? melhor[0] ?? nos[0];
}
